import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTheme } from '../../contexts/ThemeContext'
import { NavItem } from '../../config/navigation'
import 'primeicons/primeicons.css'

interface NavigationMenuProps {
  items: NavItem[]
  variant?: 'desktop' | 'mobile'
  onLinkClick?: () => void
}

export default function NavigationMenu({ items, variant = 'desktop', onLinkClick }: NavigationMenuProps) {
  const location = useLocation()
  const pathname = location.pathname
  const { theme } = useTheme()
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)

  if (variant === 'mobile') {
    return (
      <div className="flex flex-col gap-2 pb-2">
        {items.map((item) => {
          const isActive = pathname === item.href
          const hasSubmenu = item.submenu && item.submenu.length > 0
          const isDropdownOpen = openDropdown === item.href

          return (
            <div key={item.href}>
              {hasSubmenu ? (
                <>
                  <button
                    onClick={() => setOpenDropdown(isDropdownOpen ? null : item.href)}
                    className={`w-full flex items-center justify-between gap-3 py-3 px-4 rounded-lg border-l-4 transition-all duration-300 ${theme === 'dark'
                      ? 'border-transparent text-white hover:border-gold-500 hover:text-gold-400 hover:bg-gold-900/20'
                      : 'border-transparent text-gray-700 hover:border-gold-500 hover:text-gray-900 hover:bg-gold-400/20'
                      }`}
                  >
                    <div className="flex items-center gap-3">
                      {item.icon && (
                        <i className={`${item.icon} text-lg transition-transform duration-300`} />
                      )}
                      <span className="font-semibold">{item.name}</span>
                    </div>
                    <i className={`pi ${isDropdownOpen ? 'pi-angle-up' : 'pi-angle-down'} transition-transform duration-300`} />
                  </button>
                  {isDropdownOpen && (
                    <div className="ml-4 mt-2 space-y-1">
                      {item.submenu?.map((subItem) => (
                        <Link
                          key={subItem.href}
                          to={subItem.href}
                          onClick={onLinkClick}
                          className={`flex items-center gap-3 py-2 px-4 rounded-lg transition-all duration-300 ${theme === 'dark'
                            ? 'text-gold-400 hover:text-gold-300 hover:bg-gold-900/20'
                            : 'text-gray-700 hover:text-gray-900 hover:bg-gold-400/20'
                            }`}
                        >
                          {subItem.icon && (
                            <i className={`${subItem.icon} text-sm`} />
                          )}
                          <span className="text-sm font-medium">{subItem.name}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Link
                  to={item.href}
                  onClick={onLinkClick}
                  className={`flex items-center gap-3 py-3 px-4 rounded-lg border-l-4 transition-all duration-300 ${theme === 'dark'
                    ? isActive
                      ? 'border-gold-400 text-gold-400 font-bold bg-gold-900/20'
                      : 'border-transparent text-white hover:border-gold-500 hover:text-gold-400 hover:bg-gold-900/20 active:bg-gold-900/30'
                    : isActive
                      ? 'border-gold-500 text-gold-600 font-bold bg-gold-400/15'
                      : 'border-transparent text-gray-700 hover:border-gold-500 hover:text-gray-900 hover:bg-gold-400/20 active:bg-gold-400/30'
                    }`}
                >
                  {item.icon && (
                    <i className={`${item.icon} text-lg transition-transform duration-300 hover:scale-110`} />
                  )}
                  <span className="font-semibold">{item.name}</span>

                </Link>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <nav className="flex items-center gap-1 xl:gap-1.5">
      {items.map((item) => {
        const isActive = pathname === item.href
        const hasSubmenu = item.submenu && item.submenu.length > 0

        return (
          <div key={item.href} className="flex-shrink-0 relative group">
            {hasSubmenu ? (
              <div
                className={`relative flex items-center justify-center gap-1 px-2.5 xl:px-3 py-2 rounded-lg transition-all duration-300 cursor-pointer ${isActive
                  ? 'bg-gold-gradient text-white font-bold'
                  : theme === 'dark'
                    ? 'text-white hover:text-gold-400'
                    : 'text-gray-700 hover:text-gray-900'
                  }`}
              >
                {item.icon && (
                  <i
                    className={`${item.icon} text-xs xl:text-sm transition-transform duration-300 group-hover:scale-110 hidden xl:inline-block`}
                  />
                )}
                <span className="font-semibold text-[10px] xl:text-xs whitespace-nowrap">
                  {item.name}
                </span>
                <i className="pi pi-angle-down text-[8px] xl:text-[10px] ml-0.5 transition-transform duration-300 group-hover:rotate-180" />
              </div>
            ) : (
              <Link
                to={item.href}
                className={`relative flex items-center justify-center gap-1 px-2.5 xl:px-3 py-2 rounded-lg transition-all duration-300 ${isActive
                  ? 'bg-gold-gradient text-white font-bold'
                  : theme === 'dark'
                    ? 'text-white hover:text-gold-400'
                    : 'text-gray-700 hover:text-gray-900'
                  }`}
              >
                {item.icon && (
                  <i
                    className={`${item.icon} text-xs xl:text-sm transition-transform duration-300 group-hover:scale-110 hidden xl:inline-block`}
                  />
                )}
                <span className="font-semibold text-[10px] xl:text-xs whitespace-nowrap">
                  {item.name}
                </span>
              </Link>
            )}

            {/* Dropdown Menu */}
            {hasSubmenu && (
              <div className="absolute top-full left-0 mt-2 w-48 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                <div className={`${theme === 'dark' ? 'bg-black/95 border-2 border-gold-500/30' : 'bg-white border-2 border-gold-400/30'} rounded-lg shadow-xl overflow-hidden backdrop-blur-md`}>
                  {item.submenu?.map((subItem) => (
                    <Link
                      key={subItem.href}
                      to={subItem.href}
                      className={`flex items-center gap-2 px-4 py-3 transition-all duration-300 ${theme === 'dark'
                        ? 'text-white hover:text-gold-400 hover:bg-gold-900/20'
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gold-400/20'
                        }`}
                    >
                      {subItem.icon && (
                        <i className={`${subItem.icon} text-sm`} />
                      )}
                      <span className="text-sm font-medium">{subItem.name}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )
      })}
    </nav>
  )
}

